import { useEffect, useRef, useState } from 'react';
import { ScrollArea } from '@mantine/core';
import { useNavigate, useParams } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { BsArrowLeft } from 'react-icons/bs';
import { BiDownload } from 'react-icons/bi';
import { TbFileInvoice } from 'react-icons/tb';
import { CreditCard, ReceiptText, Ticket } from 'lucide-react';
// @ts-ignore
import html2pdf from 'html2pdf.js';

import { useHotelData } from 'libs/data-access';
import { getUserRole, showToast } from 'utils/utility';
import { IHotelOnlineBookingList } from 'libs/data-access/types';
import HotelFareSummary from 'libs/ui/src/FareSummary/HotelFareSummary';
import { EButton, ELoadingOverlay, EModal, ETooltip, ETable, ECard } from 'libs/ui';

import HotelDetails from './Components/HotelDetails';
import ResolveTicket from './Components/HotelTicketActions/ResolveTicket';
import MarkAsToBeRefunded from './Components/HotelTicketActions/MarkAsToBeRefunded';
import HotelTicketTimeline from './Components/HotelTicketTimeline';
import RefundTicket from '../../../Components/ManageRefund/RefundTicket';
import EditHotelRefundCharges from './Components/HotelTicketActions/EditHotelRefundCharges';
import RefundStatusTimeline from './Components/HotelTicketActions/RefundStatusTimeline';
import GenerateVoucher from './Components/HotelTicketActions/GenerateVoucher';
import HotelVoucher from './Components/HotelVoucher';
import HotelTicketInvoice from './Components/HotelTicketInvoice';
import RoomDetailsCard from './Components/RoomDetailsCard';
import HotelRoomDetails from './Components/HotelRoomDetails';
import ExternalAPILog from 'Pages/Booking/OnlineBooking/Components/ExternalAPILog/ExternalAPILog';


export default function HotelBookingDetailsHB() {

    const navigate = useNavigate();
    const { id } = useParams();
    // const location = useLocation();
    // const { state } = location;

    // const hotelTicket: IHotelOnlineBookingList = state.eachBooking;

    const { getHotelData, postHotelData } = useHotelData();

    const voucherRef: any = useRef(null);
    const invoiceRef: any = useRef(null);


    const [showVoucher, { open: openVoucher, close: closeVoucher }] = useDisclosure(false);
    const [showInvoice, { open: openInvoice, close: closeInvoice }] = useDisclosure(false);
    const [showTimeline, { open: openTimeline, close: closeTimeline }] = useDisclosure(false);
    const [confirmCancellation, { open: openCancellation, close: closeCancellation }] = useDisclosure(false);
    const [showRefundStatus, { open: openRefundStatus, close: closeRefundStatus }] = useDisclosure(false);


    const [hotelTicket, setHotelTicket] = useState<IHotelOnlineBookingList>({} as IHotelOnlineBookingList);
    const [isLoadingHotelTicket, setIsLoadingHotelTicket] = useState<boolean>(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [hasRefundProcess, setHasRefundProcess] = useState<boolean>(false);


    useEffect(() => {
        id && getHotelDetails();
    }, [id]);


    // HANDLER FUNCTIONS
    const handlePrintVoucher = () => {
        var element = voucherRef.current;

        html2pdf()
            .set({ top: 2 })
            .from(element)
            .save(`${hotelTicket.referenceNo && `Hotel Ref ${hotelTicket.referenceNo} `} e-Voucher Date ${hotelTicket.bookingDate}.pdf`);
    };

    const handlePrintTicketInvoice = () => {
        var element = invoiceRef.current;

        html2pdf()
            .set({ top: 2 })
            .from(element)
            .save(`${hotelTicket.referenceNo && `Hotel Ref ${hotelTicket.referenceNo} `}e-Invoice ${hotelTicket.invoiceNo.replaceAll("/", "_")} Date ${hotelTicket.bookingDate}.pdf`);
    };


    // API CALLS
    const getHotelDetails = async () => {
        setIsLoadingHotelTicket(true);

        const response = await getHotelData(`/booking/${id}`);

        if (response.isSuccess) {
            // console.log("Response for getting hotel details: ", response.data);
            setHotelTicket(response.data?.data);
        } else {
            console.log("Error while getting hotel details: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while getting hotel details!"
            });
        };

        setIsLoadingHotelTicket(false);
    };

    const handleCancellation = async () => {

        if (
            hotelTicket.bookingStatus !== "SUCCESS"
            &&
            hotelTicket.bookingStatus !== "TICKETING_PENDING"
        ) {
            showToast({
                type: "warning",
                message: "Only a successful booking can be cancelled!"
            });
            return;
        };

        setIsCancelling(true);

        const response = await postHotelData(`/booking/${hotelTicket.id}/cancel-booking?number=${hotelTicket.referenceNo}`, null);
        if (response.isSuccess) {
            // console.log("Response for hotel cancellation: ", response.data);
            showToast({
                type: "success",
                title: `Cancellation Successful! ${hotelTicket.referenceNo}`,
                message: "Hotel booking cancelled successfully!"
            });
            closeCancellation();
            getHotelDetails();
        } else {
            console.log("Error while cancelling hotel booking: ", response);
            showToast({
                type: "error",
                title: "Cancellation Failed!",
                message: response.message || "Could not cancel the booking!"
            });
        };

        setIsCancelling(false);
    };

    // Handle refund status check
    const handleRefundStatusCheck = (hasProcess: boolean) => {
        setHasRefundProcess(hasProcess);
    };


    return (
        <>
            {
                isLoadingHotelTicket
                    ?
                    <ELoadingOverlay />
                    :
                    <div className='mt-2'>
                        {/* TOP CONTROLS */}
                        <div className="flex flex-col md:flex-row justify-between gap-4 mb-6 p-2 bg-gray-50 rounded-lg shadow-sm">
                            <EButton
                                size="sm"
                                variant="outline"
                                leftSection={<BsArrowLeft />}
                                onClick={() => navigate(-1)}
                                className="self-start"
                            >
                                Back to Bookings
                            </EButton>

                            <div className="flex flex-wrap gap-2">
                                <div className="flex gap-2">
                                    {/* DOWNLOAD E-VOUCHER */}
                                    {
                                        hotelTicket.bookingStatus === "SUCCESS"
                                            ?
                                            <ETooltip label="Download e-Ticket">
                                                <EButton
                                                    size="sm"
                                                    variant="light"
                                                    color="blue"
                                                    onClick={openVoucher}
                                                >
                                                    <Ticket size={20} className="mr-1" />
                                                    <span className="hidden sm:inline">Voucher</span>
                                                </EButton>
                                            </ETooltip>
                                            :
                                            <></>
                                    }

                                    {/* DOWNLOAD PAYMENT ADVICE (INVOICE) */}
                                    <ETooltip label="Download e-Invoice">
                                        <EButton
                                            size="sm"
                                            variant="light"
                                            color="blue"
                                            onClick={openInvoice}
                                        >
                                            <TbFileInvoice size={20} className="mr-1" />
                                            <span className="hidden sm:inline">Payment Advice</span>
                                        </EButton>
                                    </ETooltip>

                                    {/* VIEW TICKET TIMELINE */}
                                    <ETooltip label="View Timeline">
                                        <EButton
                                            size="sm"
                                            variant="light"
                                            color="blue"
                                            onClick={openTimeline}
                                        >
                                            <ReceiptText size={20} className="mr-1" />
                                            <span className="hidden sm:inline">Timeline</span>
                                        </EButton>
                                    </ETooltip>

                                    {/* <ExternalAPILog traceId={hotelTicket?.traceId} /> */}

                                    {/* VIEW REFUND STATUS (TIMELINE) */}
                                    {(hotelTicket?.bookingStatus === "TO_BE_REFUNDED" || hotelTicket?.bookingStatus === "REFUNDED") && getUserRole().includes("ADMIN")
                                        ? (
                                            <ETooltip label="View Refund Status">
                                                <EButton
                                                    size="sm"
                                                    variant="light"
                                                    color="blue"
                                                    onClick={openRefundStatus}
                                                >
                                                    <CreditCard size={20} className="mr-1" />
                                                    <span className="hidden sm:inline">Refund Status</span>
                                                </EButton>
                                            </ETooltip>
                                        )
                                        :
                                        <></>}
                                </div>

                                {/* REFUND MANAGEMENT */}
                                {getUserRole().includes("ADMIN")
                                    ?
                                    <div className="flex gap-2">
                                        {hotelTicket?.bookingStatus === "TO_BE_REFUNDED" || hotelTicket?.bookingStatus === "REFUNDED" ? (
                                            hasRefundProcess ? (
                                                <ETooltip label="Refund process already initiated, cannot edit charges">
                                                    <div>
                                                        <EButton
                                                            size="sm"
                                                            color="gray"
                                                            variant="light"
                                                            disabled={true}
                                                        >
                                                            <span>Edit Refund Charges</span>
                                                        </EButton>
                                                    </div>
                                                </ETooltip>
                                            ) : (
                                                <ETooltip label="Edit Refund Charges">
                                                    <EditHotelRefundCharges
                                                        hotelTicket={hotelTicket}
                                                        handleRefresh={() => getHotelDetails()}
                                                    />
                                                </ETooltip>
                                            )
                                        ) : (
                                            <MarkAsToBeRefunded
                                                label={hotelTicket?.bookingStatus === "FAILED" ? "Reject" : "Mark as To be Refunded"}
                                                hotelTicket={hotelTicket}
                                                handleRefresh={() => getHotelDetails()}
                                            />
                                        )}

                                        {/* RESOLVE TICKET */}
                                        <ResolveTicket
                                            hotelTicket={hotelTicket}
                                            handleRefresh={() => getHotelDetails()}
                                        />

                                        {/* VOUCHERING */}
                                        {hotelTicket.bookingStatus === "TICKETING_PENDING"
                                            ?
                                            <ETooltip label="Generate Voucher">
                                                <GenerateVoucher
                                                    hotelTicket={hotelTicket}
                                                    handleRefresh={() => getHotelDetails()}
                                                />
                                            </ETooltip>
                                            :
                                            <></>}
                                    </div>
                                    :
                                    <></>}

                                {getUserRole().includes("ADMIN")
                                    ?
                                    <div className="flex gap-2">
                                        {hotelTicket?.id && hotelTicket.bookingStatus === "TO_BE_REFUNDED" && (
                                            hasRefundProcess ? (
                                                <ETooltip label="Refund process already initiated">
                                                    <div>
                                                        <EButton
                                                            size="sm"
                                                            color="gray"
                                                            variant="light"
                                                            disabled={true}
                                                        >
                                                            <span>Refund</span>
                                                        </EButton>
                                                    </div>
                                                </ETooltip>
                                            ) : (
                                                <RefundTicket
                                                    service="HOTEL"
                                                    bookingId={String(hotelTicket.id)}
                                                    handleRefresh={() => getHotelDetails()}
                                                />
                                            )
                                        )}

                                        {/* CANCEL BOOKING */}
                                        {hotelTicket?.bookingStatus === "SUCCESS" || hotelTicket?.bookingStatus === "TICKETING_PENDING"
                                            ?
                                            (
                                                <EButton
                                                    size="sm"
                                                    color="yellow"
                                                    variant="light"
                                                    loading={isCancelling}
                                                    onClick={openCancellation}
                                                >
                                                    <span>Cancel</span>
                                                </EButton>
                                            )
                                            :
                                            <></>}
                                    </div>
                                    :
                                    <></>}
                            </div>
                        </div>

                        {/* HOTEL DETAILS & GUEST DETAILS */}
                        <div className="grid grid-cols-12 gap-6">
                            <div className="cols-span-12 sm:col-span-8">
                                {/* Hotel Details */}
                                <ECard title="Hotel Details">
                                    <HotelDetails hotel={hotelTicket} />
                                </ECard>

                                {/* GUEST DETAILS */}
                                <div className="mt-6">
                                    <ECard title="Guest Details">
                                        <div>
                                            <ETable>
                                                <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>PAN / Passport</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        hotelTicket?.bookingRooms?.map((item, index) => (
                                                            <> {
                                                                item?.guestList?.map((guest, index) => (
                                                                    <tr key={index}>
                                                                        <td className="font-medium">
                                                                            <span className='capitalize'>{guest?.salutation?.toLowerCase()}</span> {guest?.firstName} {guest?.lastName}
                                                                        </td>
                                                                        <td>{guest?.panCardNo ? guest?.panCardNo : guest?.passPortNumber ?? '-'}</td>
                                                                    </tr>
                                                                ))
                                                            }
                                                            </>
                                                        ))
                                                    }
                                                </tbody>
                                            </ETable>
                                        </div>
                                    </ECard>
                                </div>

                                {/* Hotel Room Details */}
                                <div className="mt-6">
                                    <ECard title="Room Details">
                                        <HotelRoomDetails hotel={hotelTicket?.bookingRooms} showCarousel={false} />
                                        {/* {
                                            hotelTicket?.bookingRooms?.map(
                                                (roomRate, index) =>
                                                    <RoomDetailsCard
                                                        key={`${index}_room`}
                                                        roomRate={roomRate}
                                                    />
                                            )
                                        } */}
                                    </ECard>
                                </div>
                            </div>

                            <div className="col-span-12 md:col-span-4">
                                <ECard title="Fare Summary">
                                    {
                                        hotelTicket.id
                                            ?
                                            <HotelFareSummary
                                                supplierId={0}
                                                hotelOnlineBookingPayment={hotelTicket?.paymentOrderResponse}
                                                refundCharges={[{ ...hotelTicket.cancellationChargeResponse, bookingId: hotelTicket.id }]}
                                            />
                                            :
                                            <></>
                                    }
                                </ECard>
                            </div>
                        </div>
                    </div>
            }

            {/* VOUCHER */}
            <EModal
                size="80%"
                scrollAreaComponent={ScrollArea.Autosize}
                opened={showVoucher}
                onClose={() => {
                    closeVoucher();
                }}
                title={
                    <div className="flex justify-between items-center">
                        <EButton
                            variant="subtle"
                            rightSection={<BiDownload />}
                            onClick={handlePrintVoucher}
                        >Download</EButton>
                    </div>
                }
            >
                <div
                    className="bg-gradient-to-b from-white to-blue-50"
                    ref={voucherRef}
                >
                    <HotelVoucher hotelTicket={hotelTicket} />
                </div>
            </EModal>

            {/* PAYMENT ADVICE (INVOICE) */}
            <EModal
                size="80%"
                scrollAreaComponent={ScrollArea.Autosize}
                opened={showInvoice}
                onClose={() => {
                    closeInvoice();
                }}
                title={
                    <div className="flex justify-between items-center">
                        <EButton
                            variant="subtle"
                            rightSection={<BiDownload />}
                            onClick={handlePrintTicketInvoice}
                        >Download</EButton>
                    </div>
                }
            >
                <div
                    className="p-4 bg-gradient-to-b from-white to-blue-50"
                    ref={invoiceRef}
                >
                    <HotelTicketInvoice ticketDetails={hotelTicket} />
                </div>
            </EModal>

            {/* CANCEL BOOKING CONFIRMATION MODAL */}
            <EModal
                opened={confirmCancellation}
                onClose={closeCancellation}
            >
                <div className="flex flex-col gap-6">
                    <span className="font-semibold text-lg">Are you sure you want to cancel this booking?</span>

                    <div className="flex justify-end gap-2">
                        <EButton
                            autoContrast
                            size="sm"
                            disabled={isCancelling}
                            onClick={closeCancellation}
                        >No</EButton>
                        <EButton
                            size="sm"
                            color="red"
                            variant="light"
                            loading={isCancelling}
                            onClick={handleCancellation}
                        >Yes</EButton>
                    </div>
                </div>
            </EModal>

            {/* TICKET TIMELINE */}
            <HotelTicketTimeline
                hotelTicket={hotelTicket}
                opened={showTimeline}
                onClose={() => closeTimeline()}
            />

            {/* REFUND STATUS TIMELINE */}
            <RefundStatusTimeline
                bookingId={hotelTicket.id || ""}
                opened={showRefundStatus}
                onClose={closeRefundStatus}
                onRefundStatusCheck={handleRefundStatusCheck}
            />
        </>
    )
}
